import React, { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';
import { Loader2 } from 'lucide-react';

/**
 * Two modes of use:
 *   1. String mode (legacy) — options are plain strings; the string is
 *      both the label AND the value written back to the parent via
 *      onChange.
 *   2. Object mode (Feb 2026) — options are `{ label, value }`. The
 *      label shows in the dropdown + input; `value` (typically a UUID)
 *      is what gets written back via onChange. The parent supplies
 *      `displayValue` so that when editing an existing record (where
 *      we only have the id) we can still show the human-readable label.
 *      Falls back to `value` (the id) when no displayValue is provided.
 */
export const Autocomplete = ({ value, onChange, fetchOptions, fetchDefaults, placeholder, className, displayValue, 'data-testid': testId }) => {
  // The visible input text — the label the user sees. Decoupled from
  // `value` because in object-mode the parent stores an id but we want
  // to render the label. In string-mode `displayValue` isn't passed
  // and we fall back to `value` (backwards compat).
  const [query, setQuery] = useState(displayValue ?? value ?? '');
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => { setQuery(displayValue ?? value ?? ''); }, [value, displayValue]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInput = (val) => {
    setQuery(val);
    // In object-mode a raw typed string isn't a valid id — do NOT commit
    // it to the parent. Only committed on select from the dropdown.
    // Backwards-compat: string-mode still commits every keystroke.
    if (!_isObjectMode(options)) {
      onChange(val);
    } else if (val === '') {
      // Explicit clear — parent should know the id was unset.
      onChange('');
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.length < 1) {
      // Show defaults when input is cleared
      if (fetchDefaults) {
        loadDefaults();
      } else {
        setOptions([]); setOpen(false);
      }
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const results = await fetchOptions(val);
        setOptions(results);
        setOpen(results.length > 0);
      } catch { setOptions([]); }
      finally { setLoading(false); }
    }, 250);
  };

  const loadDefaults = async () => {
    if (!fetchDefaults) return;
    setLoading(true);
    try {
      const results = await fetchDefaults();
      setOptions(results);
      setOpen(results.length > 0);
    } catch { setOptions([]); }
    finally { setLoading(false); }
  };

  const handleFocus = () => {
    if (options.length > 0) {
      setOpen(true);
    } else if (fetchDefaults && !query) {
      loadDefaults();
    }
  };

// True if `options` are `{label, value}` objects rather than strings.
const _isObjectMode = (options) =>
  Array.isArray(options) && options.length > 0
  && typeof options[0] === 'object' && options[0] !== null
  && 'value' in options[0];

  const handleSelect = (item) => {
    // Object-mode: show label, commit value (typically a UUID) AND pass
    // the full option object as the 2nd arg so the parent can capture
    // the label (e.g. for a `displayValue` mirror on the next render).
    // String-mode: label === value, keep legacy behaviour.
    if (item && typeof item === 'object' && 'value' in item) {
      setQuery(item.label);
      onChange(item.value, item);
    } else {
      setQuery(item);
      onChange(item);
    }
    setOpen(false);
  };

  return (
    <div ref={wrapperRef} className={`relative ${className || ''}`}>
      <Input
        value={query}
        onChange={e => handleInput(e.target.value)}
        onFocus={handleFocus}
        placeholder={placeholder}
        data-testid={testId}
      />
      {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-lg max-h-48 overflow-auto" data-testid={`${testId}-dropdown`}>
          {options.map((item, i) => {
            const label = (item && typeof item === 'object' && 'label' in item) ? item.label : item;
            return (
              <button
                key={i}
                type="button"
                className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50 transition-colors"
                onClick={() => handleSelect(item)}
                data-testid={`${testId}-option-${i}`}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
